import { useRef, useMemo } from "react";
import { useForm, useController } from "react-hook-form";
import { MdOutlineAddBox } from "react-icons/md";

import OverlayTooltip from "../../../OverlayTooltip";

import DialogModal from "../../../DialogModal";
import { Input } from "../../../Utility/FormElements";

import { cn } from "../../../../helpers/cn";

import AutoCompleteReactSelect from "../../../AutoCompleteReactSelect";

import {
  useGetAllCustomerWithoutPermissions,
  useCreateCustomerReturnPermission,
  createdReturnPermissionKeys,
  createReturnPermissionsOptions,
  useIsFetchingGlobalReturnPermissions,
  useGetGlobalReturnPermissionsCacheData,
} from "../returnPermission.util";

const formID = "create-permission-form";

function CustomerWithoutPermissionAutoComplete({ control, name }) {
  const { data: customerWithoutPermission, isLoading } =
    useGetAllCustomerWithoutPermissions();

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: { required: true },
  });

  const customerWithoutPermissionOptions = useMemo(
    () =>
      customerWithoutPermission.map((customer) => ({
        value: customer.tranid,
        label: `${customer.customer} - ${customer.address}`,
        ...customer,
      })),
    [customerWithoutPermission]
  );

  return (
    <>
      <div className={`tw-mt-1 ${error ? "tw-text-red-500" : ""}`}>
        Customer *
      </div>

      <AutoCompleteReactSelect
        isLoading={isLoading}
        isError={Boolean(error)}
        options={customerWithoutPermissionOptions}
        value={value}
        onChange={onChange}
      />

      {error && <small className="tw-text-red-500 tw-m-0">Required</small>}
    </>
  );
}

function ReasonEnteredInput({ register, errors }) {
  return (
    <>
      <Input
        id="reason-entered"
        label="Reason Entered *"
        labelProps={{ className: "tw-mt-1" }}
        {...register("reasonEntered", {
          required: true,
          validate: (value) => value.trim() !== "",
        })}
        isError={errors.reasonEntered}
        helperText={errors.reasonEntered && "Required"}
      />

      <div className={`tw-mt-1 ${errors.permission ? "tw-text-red-500" : ""}`}>
        Permission *
      </div>
    </>
  );
}

function PermissionSelect({ register, errors }) {
  return (
    <>
      <select
        className={`form-control ${
          errors.permission ? "tw-border-red-500 focus:tw-border-red-500" : ""
        }`}
        {...register("permission", { required: true })}
      >
        <option value="">Select Permission</option>
        {createReturnPermissionsOptions.map((option) => (
          <option key={option.value} value={option.label}>
            {option.label}
          </option>
        ))}
      </select>

      {errors?.permission && (
        <small className="tw-text-red-500 tw-m-0">Required</small>
      )}
    </>
  );
}

function CreatePermission() {
  /**
   * @type {React.MutableRefObject<{show: function():void, hide: function():void} | null>} dialogModalRef
   */
  const dialogModalRef = useRef(null);

  const { createMutation: createPermissions, isLoading: isCreatingPermission } =
    useCreateCustomerReturnPermission();

  const isLoading = useIsFetchingGlobalReturnPermissions();
  const currentGlobalReturnPermission =
    useGetGlobalReturnPermissionsCacheData();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm({
    defaultValues: {
      customer: [],
      clientId: "",
      enteredBy: "",
      reasonEntered: "",
      permission: "",
    },
  });

  const onCancel = () => {
    reset();
    dialogModalRef.current?.hide();
  };

  const onSubmit = (submitData) => {
    const formatSubmitData = submitData.customer.map((customer) => ({
      CustomerId: customer.tranid,
      ReasonEntered: submitData.reasonEntered,
      EnteredDate: new Date().toLocaleString(),
    }));

    if (
      submitData.permission ===
      createdReturnPermissionKeys.allowedReturnByApproval
    ) {
      const formatRequestData = formatSubmitData.map((data) => ({
        ...data,
        ByApproval: true,
        IsActive: true,
      }));

      createPermissions(formatRequestData);
    }

    if (
      submitData.permission === createdReturnPermissionKeys.notAllowedReturns
    ) {
      const formatRequestData = formatSubmitData.map((data) => ({
        ...data,
        ByApproval: false,
        IsActive: true,
      }));

      createPermissions(formatRequestData);
    }

    reset();
    dialogModalRef.current?.hide();
  };

  const getTooltipText = () => {
    if (isLoading) return "Loading...";

    if (currentGlobalReturnPermission?.isDisabled)
      return "Returns disabled globally";

    return isCreatingPermission
      ? "Creating Permission..."
      : "Create Permission";
  };

  return (
    <div>
      <OverlayTooltip label={getTooltipText()}>
        <button
          type="button"
          className={cn(
            "btn tw-border-0 tw-outline-none tw-grid tw-place-content-center hover:tw-bg-gray-200 tw-px-2",
            isCreatingPermission ? "tw-cursor-not-allowed tw-bg-gray-200" : ""
          )}
          onClick={() => {
            if (
              isCreatingPermission ||
              currentGlobalReturnPermission?.isDisabled ||
              currentGlobalReturnPermission?.isByApproval
            )
              return;
            dialogModalRef.current?.show();
          }}
          disabled={
            currentGlobalReturnPermission?.isDisabled ||
            currentGlobalReturnPermission?.isByApproval
          }
        >
          <MdOutlineAddBox className="tw-h-6 tw-w-6" />
        </button>
      </OverlayTooltip>

      <DialogModal
        ref={dialogModalRef}
        title="New Customer Permission"
        cancelButtonProps={{ onClick: onCancel }}
        saveButtonProps={{ type: "submit", form: formID, onClick: undefined }}
        onHide={() => {}}
        size="lg"
      >
        <form id={formID} onSubmit={handleSubmit(onSubmit)}>
          <CustomerWithoutPermissionAutoComplete
            control={control}
            name="customer"
          />

          <ReasonEnteredInput register={register} errors={errors} />

          <PermissionSelect register={register} errors={errors} />
        </form>
      </DialogModal>
    </div>
  );
}

export default CreatePermission;
